import { useState, useRef, useEffect, useCallback, useMemo, type RefObject } from 'react'
import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import AppIcon from '@/components/content/app/AppIcon'
import ReviewSidebar from '@/components/content/reviews/review/ReviewSidebar'
import Tree from '@/components/content/sidebar/Tree'
import { PageSelectList, collections } from '@/components/ui/PageSelect'
import { Icon } from '@/icons/Icon'
import { getBrandColors, cn, scrollToSection, hasRichTextContent, toSlug } from '@/lib/utils'
import { easeMedium } from '@/lib/transitions'
import { useScrollGradients } from '@/hooks/useScrollGradients'
import { usePageContext } from '@/components/content/common/PageContext'
import { generateStructure } from '@/lib/content'
import { generateMenu } from '@/helpers/menus'
import type { Block, SectionItem } from '@/types/app'
import type { App, Category, AppCategoryRecommendation, Review, Deal } from '@/payload-types'

type BestSidebarProps = {
  id: string
  apps: App[]
  categories: Pick<Category, 'id' | 'name' | 'slug' | 'icon'>[]
  category?: Category
  trigger?: boolean
  showOtherApps?: boolean
  showVerdict?: boolean
  showVideo?: boolean
  onMenuClose?: () => void
}

export type ViewportSize = 'mobile' | 'tablet' | 'desktop'

type AppsToggleProps = {
  apps: App[]
  showOtherApps?: boolean
  showVerdict?: boolean
  showVideo?: boolean
  size: ViewportSize
}

type CategoryToggleProps = {
  category: Category
  setMenu: (menu: boolean) => void
  menu: boolean
  size: ViewportSize
}

type SidebarLinkProps = {
  icon: string
  label: string
  anchor: string
  size: ViewportSize
  active?: boolean
}

function addBestSummarySlugs(app: App, sections: SectionItem[]): SectionItem[] {
  const appSlug = toSlug(app.name)

  const addChildSlugs = (parentSlug: string, children?: SectionItem[]): SectionItem[] | undefined =>
    children?.map((child, index) => {
      const childSlug = toSlug(child.title || `section-${index}`)
      const childFullSlug = parentSlug && childSlug ? `${parentSlug}-${childSlug}` : child.slug

      return {
        ...child,
        slug: childFullSlug,
        children: addChildSlugs(childFullSlug ?? '', child.children),
      }
    })

  return sections.map((section, index) => {
    const rootSlug = toSlug(section.title || `section-${index}`)
    const rootFullSlug = appSlug && rootSlug ? `${appSlug}-${rootSlug}` : section.slug

    return {
      ...section,
      slug: rootFullSlug,
      children: addChildSlugs(rootFullSlug ?? '', section.children),
    }
  })
}

export default function BestSidebar({
  id,
  apps,
  categories,
  category,
  trigger = false,
  showOtherApps,
  showVerdict,
  showVideo,
  onMenuClose,
}: BestSidebarProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { activeApp } = usePageContext()

  const [selectedApp, setSelectedApp] = useState<App | null>(apps.length > 0 ? apps[0] : null)
  const [categoryMenu, setCategoryMenu] = useState(false)
  const [top, setTop] = useState(0)
  const [size, setSize] = useState<ViewportSize>('tablet')

  const hasApps = apps && apps.length > 0

  const roots = useMemo(
    () => [
      {
        title: id,
        slug: toSlug(id),
        icon: 'ranking',
        app: null,
        menu: [],
      },
      ...(showVideo
        ? [
            {
              title: 'Video',
              slug: 'video',
              icon: 'circle-play',
              app: null,
              menu: [],
            },
          ]
        : []),
      ...apps.map((app) => {
        const [review] = (app?.review?.docs || []) as Review[]
        const appCategoryRecommendations = app.categoryRecommendations
          ?.docs as AppCategoryRecommendation[]
        const appCategory = appCategoryRecommendations?.find((acr) => {
          return typeof acr.category === 'number'
            ? acr.category === category?.id
            : (acr.category as Category).id === category?.id
        })

        const acrSummary = appCategory?.summary?.root?.children as Block[] | undefined
        const reviewSummary = review?.summary?.root?.children as Block[] | undefined
        const summary = hasRichTextContent(acrSummary) ? acrSummary : reviewSummary

        const { roots: structure } = summary
          ? generateStructure({ content: summary })
          : { roots: [] }
        const deals = app.deals?.docs as Deal[]
        const menu = generateMenu('app', { app, deals })

        return {
          title: app.name,
          slug: app.slug,
          app: app,
          children: addBestSummarySlugs(app, structure),
          depth: 'root' as const,
          menu,
        }
      }),
      ...(showVerdict
        ? [
            {
              title: 'Our Final Verdict',
              slug: 'our-final-verdict',
              icon: 'best',
              app: null,
              menu: [],
            },
          ]
        : []),
      ...(showOtherApps
        ? [
            {
              title: 'Apps Worth Trying',
              slug: 'apps-worth-trying',
              icon: 'asterisk',
              app: null,
              menu: [],
            },
          ]
        : []),
    ],
    [id, apps, category?.id, showVideo, showOtherApps, showVerdict],
  )

  const selectedAppMenu = useMemo(() => {
    if (!selectedApp) return []

    const root = roots.find((root) => root.app && root.app.slug === selectedApp.slug)

    return root?.menu ?? []
  }, [roots, selectedApp])

  useEffect(() => {
    if (activeApp) {
      const app = apps.find((app) => app.slug === activeApp)
      if (app) {
        setSelectedApp(app)
      } else {
        setSelectedApp(null)
      }
    }
  }, [activeApp, apps])

  const handleMenuClose = useCallback(() => {
    setCategoryMenu(false)
    if (onMenuClose) onMenuClose()
  }, [setCategoryMenu, onMenuClose])

  useEffect(() => {
    if (trigger) {
      setCategoryMenu(true)
    }
  }, [trigger])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && categoryMenu) {
        handleMenuClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [categoryMenu, handleMenuClose])

  useEffect(() => {
    const handleResize = () => {
      setSize(window.innerWidth < 768 ? 'mobile' : window.innerWidth >= 1536 ? 'desktop' : 'tablet')

      if (containerRef.current) {
        setTop(containerRef.current.getBoundingClientRect().top)
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const categoryPlaceholder: Category = {
    id: 0,
    name: 'Select Category',
    icon: 'layers-3',
    slug: 'select-category',
    lastPublishedAt: '',
    updatedAt: '',
    createdAt: '',
  }

  return (
    <>
      {size === 'mobile' ? (
        <Motion key="best-sidebar-mobile">
          <m.div
            key="mobile-sidebar"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={easeMedium}
            className="fixed left-0 top-20 z-40 w-full md:hidden"
          >
            <CategoryToggle
              category={category ?? categoryPlaceholder}
              setMenu={(value: boolean) => (value ? setCategoryMenu(value) : handleMenuClose())}
              menu={categoryMenu}
              size={size}
            />
            <Tree
              roots={roots}
              header={
                <ReviewSidebar
                  app={selectedApp || undefined}
                  compareWithApp={apps[0]}
                  category={category}
                  menu={selectedAppMenu}
                />
              }
              offset={140}
            />
          </m.div>
        </Motion>
      ) : (
        <Motion key="best-sidebar-desktop">
          <m.div
            ref={containerRef}
            initial={{ opacity: 0, x: 100, y: 'calc(-50% + 32px)' }}
            animate={{ opacity: 1, x: 0, y: 'calc(-50% + 32px)' }}
            exit={{ opacity: 0, x: 100, y: 'calc(-50% + 32px)' }}
            transition={easeMedium}
            className="hide-scrollbar fixed right-4 top-1/2 z-20 hidden w-fit flex-col gap-2 md:flex xl:right-6"
          >
            <CategoryToggle
              category={category ?? categoryPlaceholder}
              setMenu={(value: boolean) => (value ? setCategoryMenu(value) : handleMenuClose())}
              menu={categoryMenu}
              size={size}
            />
            {hasApps && (
              <AppsToggle
                apps={apps}
                showOtherApps={showOtherApps}
                showVerdict={showVerdict}
                showVideo={showVideo}
                size={size}
              />
            )}
          </m.div>
        </Motion>
      )}

      <AnimatePresence>
        {categoryMenu && (
          <>
            <PageSelectList
              top={top}
              options={categories}
              selected={category?.id ?? 0}
              sidebar={true}
              collection={collections.best}
            />
            <div onClick={handleMenuClose} className="fixed left-0 top-0 z-20 h-full w-full"></div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

export function AppsToggle({ apps, size, showOtherApps, showVerdict, showVideo }: AppsToggleProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { setActiveApp, activeApp, activeSection } = usePageContext()
  const { showTopGradient, showBottomGradient } = useScrollGradients(
    containerRef as RefObject<HTMLElement>,
    size === 'mobile',
  )

  const handleClick = async (slug?: App['slug']) => {
    if (!slug) return
    await scrollToSection({
      id: slug,
      offset: { mobile: 130, desktop: 80 },
      animate: true,
      hideHash: false,
    })
    setActiveApp(slug)
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container || !activeApp) return

    const selectedElement = container.querySelector(`[data-app-slug="${activeApp}"]`)
    if (!selectedElement) return

    const scrollTop =
      selectedElement.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      12
    container.scrollTo({ top: scrollTop, behavior: 'smooth' })
  }, [activeApp, size])

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border border-white/15',
        'before:pointer-events-none before:absolute before:left-0 before:top-0 before:z-10 before:h-8 before:w-full before:bg-gradient-to-b before:from-black before:to-transparent before:opacity-0 before:transition-opacity before:content-[""]',
        'after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:z-10 after:h-8 after:w-full after:bg-gradient-to-t after:from-black after:to-transparent after:opacity-0 after:transition-opacity after:content-[""]',
        showTopGradient && 'before:opacity-100',
        showBottomGradient && 'after:opacity-100',
      )}
    >
      <div
        ref={containerRef}
        className="group flex max-h-[calc(100vh-200px)] flex-col items-center gap-3 overflow-x-auto overflow-y-auto bg-white/10 p-2.5 backdrop-blur-2xl 2xl:max-h-[calc(100vh-240px)]"
      >
        <SidebarLink
          icon="ranking"
          label="App Ranking"
          anchor="top"
          size={size}
          active={
            activeSection === 'app-ranking' ||
            activeSection === 'overview' ||
            activeSection?.startsWith('overview-')
          }
        />
        {showVideo && (
          <SidebarLink
            icon="circle-play"
            label="Video"
            anchor="video"
            size={size}
            active={activeSection === 'video'}
          />
        )}

        {apps.map((app: App) => {
          const { backgroundColor } = getBrandColors(app)
          const active = activeApp === app.slug
          return (
            <div key={app.slug} data-app-slug={app.slug}>
              <AppIcon
                icon={app.icon}
                glyph={app.glyph}
                size={size === 'mobile' || size === 'desktop' ? 44 : 28}
                invert={true}
                background={backgroundColor}
                active={active}
                onActivate={() => handleClick(app.slug)}
                className={cn(
                  'cursor-pointer transition-opacity duration-300 hover:!opacity-100 group-hover:opacity-50',
                )}
              />
            </div>
          )
        })}

        {showVerdict && (
          <SidebarLink
            icon="best"
            label="Our Final Verdict"
            anchor="our-final-verdict"
            size={size}
            active={
              activeSection === 'our-final-verdict' ||
              activeSection?.startsWith('our-final-verdict-')
            }
          />
        )}

        {showOtherApps && (
          <SidebarLink
            icon="asterisk"
            label="Apps worth trying"
            anchor="apps-worth-trying"
            size={size}
            active={activeSection === 'apps-worth-trying'}
          />
        )}
      </div>
    </div>
  )
}

export function CategoryToggle({ category, setMenu, menu, size }: CategoryToggleProps) {
  return (
    <div
      onClick={() => setMenu(!menu)}
      className="flex h-[50px] w-full cursor-pointer items-center justify-between border-b border-white/15 bg-black/85 px-5 backdrop-blur-md md:h-auto md:w-auto md:cursor-auto md:flex-col md:gap-4 md:rounded-xl md:border md:bg-white/10 md:p-2.5 md:backdrop-blur-2xl"
    >
      <div
        className={cn(
          'squircle flex cursor-pointer items-center gap-3.5 text-14 text-white transition-all md:h-7 md:w-7 md:justify-center md:gap-0 md:bg-black md:bg-white/10 md:outline md:outline-[0.75px] 2xl:h-11 2xl:w-11',
          menu
            ? 'md:shadow-icon-active md:outline-1 md:outline-primary-500'
            : 'md:outline-white/30 md:hover:outline-white/50',
        )}
      >
        <Icon name={category.icon ?? ''} size={size === 'desktop' ? 20 : 14} />
        <span className="md:hidden">{category.name}</span>
      </div>

      <div
        className={cn(
          '-rotate-90 text-white transition-transform ease-in-out md:hidden',
          menu && 'rotate-90',
        )}
      >
        <Icon name="chevron-left" size={12} />
      </div>
    </div>
  )
}

export function SidebarLink({ icon, label, anchor, size, active }: SidebarLinkProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() =>
        scrollToSection({
          id: anchor,
          offset: { mobile: 130, desktop: 80 },
          animate: true,
          hideHash: false,
        })
      }
      className={cn(
        'squircle flex shrink-0 cursor-pointer items-center gap-3.5 text-14 text-white transition-all md:h-7 md:w-7 md:justify-center md:gap-0 md:bg-black md:bg-white/10 md:outline md:outline-[0.75px] md:outline-white/30 2xl:h-11 2xl:w-11',
        active
          ? 'md:shadow-icon-active md:outline-1 md:outline-primary-500'
          : 'md:hover:outline-white/50',
      )}
    >
      <Icon name={icon} size={size === 'desktop' ? 20 : 14} />
      <span className="md:hidden">{label}</span>
    </button>
  )
}
